import { Controller } from "@hotwired/stimulus"

// Media Picker — attaches a "Browse Media" button to any text field.
// Usage:
//   <input data-controller="media-picker"
//          data-media-picker-input-value="field_id"
//          data-media-picker-folder-value="carousel">
//
// Values:
//   input  (required) — DOM id of the target text field
//   folder (optional) — filter to show only images in this subfolder
//
// On click, opens a modal with the filtered media library.
// If the field already has a value, that image appears first + highlighted.
// Selecting an image populates the field and closes the modal.
export default class extends Controller {
  static targets = ["button"]
  static values = { input: String, folder: String }

  connect() {
    // Create the browse button
    const btn = document.createElement("button")
    btn.type = "button"
    btn.className = "btn btn-outline-primary btn-sm ms-2"
    btn.style.cssText = "font-size:0.65rem;padding:3px 10px;white-space:nowrap;flex-shrink:0;"
    btn.textContent = "📁 Browse"
    btn.addEventListener("click", () => this.open())

    // Wrap the element in a flex container if not already
    if (this.element.closest(".media-picker-wrapper")) return
    const wrapper = document.createElement("div")
    wrapper.className = "media-picker-wrapper d-flex align-items-center"
    this.element.parentNode.insertBefore(wrapper, this.element)
    wrapper.appendChild(this.element)
    wrapper.appendChild(btn)

    // Create the modal (once)
    if (!document.getElementById("media-picker-modal")) {
      const modal = document.createElement("div")
      modal.id = "media-picker-modal"
      modal.innerHTML = `
        <div id="media-picker-backdrop" style="display:none;position:fixed;top:0;left:0;right:0;bottom:0;z-index:99998;background:rgba(0,0,0,0.85);align-items:center;justify-content:center;">
          <div style="position:relative;width:90vw;height:90vh;background:var(--color-bg-card,#151515);border-radius:8px;overflow:hidden;display:flex;flex-direction:column;">
            <div style="display:flex;justify-content:space-between;align-items:center;padding:12px 20px;border-bottom:1px solid var(--color-border,#333);flex-shrink:0;">
              <span style="color:var(--color-text,#eee);font-weight:600;font-size:0.9rem;">Select Image</span>
              <button id="media-picker-close" style="background:none;border:none;color:var(--color-text-dim,#888);font-size:1.4rem;cursor:pointer;line-height:1;">&times;</button>
            </div>
            <div id="media-picker-body" style="flex:1;overflow-y:auto;padding:16px 20px;"></div>
            <div style="text-align:center;padding:10px 20px;border-top:1px solid var(--color-border,#333);font-size:0.7rem;color:var(--color-text-dim,#666);flex-shrink:0;">
              Click any image to select it
            </div>
          </div>
        </div>
      `
      document.body.appendChild(modal)

      document.getElementById("media-picker-close").addEventListener("click", () => this.close())
      document.getElementById("media-picker-backdrop").addEventListener("click", (e) => {
        if (e.target.id === "media-picker-backdrop") this.close()
      })
      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && this.#isOpen()) this.close()
      })
    }
  }

  async open() {
    const backdrop = document.getElementById("media-picker-backdrop")
    const body = document.getElementById("media-picker-body")
    backdrop.style.display = "flex"
    document.body.style.overflow = "hidden"
    body.innerHTML = '<div style="text-align:center;padding:40px;color:var(--color-text-dim,#888);">Loading…</div>'

    // Build query params
    const params = new URLSearchParams()
    if (this.folderValue) params.set("folder", this.folderValue)
    const target = document.getElementById(this.inputValue)
    if (target && target.value) params.set("selected", target.value)

    try {
      const resp = await fetch(`/en/media/browser?${params.toString()}`)
      const html = await resp.text()
      body.innerHTML = html
      // Attach click handlers to picker items
      body.querySelectorAll(".media-picker-item").forEach(el => {
        el.addEventListener("click", () => this.#select(el.dataset.path))
      })
    } catch {
      body.innerHTML = '<div style="text-align:center;padding:40px;color:#f55;">Failed to load media library</div>'
    }
  }

  close() {
    const backdrop = document.getElementById("media-picker-backdrop")
    backdrop.style.display = "none"
    document.body.style.overflow = ""
  }

  #select(path) {
    const target = document.getElementById(this.inputValue)
    if (target) {
      target.value = path
      target.dispatchEvent(new Event("input", { bubbles: true }))
      target.dispatchEvent(new Event("change", { bubbles: true }))
    }
    this.close()
  }

  #isOpen() {
    const b = document.getElementById("media-picker-backdrop")
    return b && b.style.display === "flex"
  }
}
